import { CmsAsset } from "@nendaTypes/cms/CmsAssetType";
import { MediaResource } from "@nendaTypes/cms/CmsMediaType";
import { hasVerimatrixDrm } from "./drmUtils";

export enum UnplayableReason {
  NO_MEDIA = "no media",
  NO_MAIN_MEDIA = "no main or channel media",
  NOT_A_STREAM = "not a DASH or HLS stream",
  INVALID_URL = "invalid link",
  VIDEO_FILE = "points to a video file, not a stream manifest",
  UPLOAD_STORAGE = "points to the upload storage",
  LINK_EXPIRED = "link expired",
  ENCRYPTED_WITHOUT_DRM = "encrypted without DRM",
  ASSET_NOT_FOUND = "asset not found",
}

export interface CmsAssetPlayability {
  playable: boolean;
  reasons: UnplayableReason[];
}

export interface AssetPlayabilityResult extends CmsAssetPlayability {
  assetId: string;
}

export interface AssetPlayabilityResponse {
  results: AssetPlayabilityResult[];
}

export interface PlayabilityContext {
  uploadStorage?: { host: string; containerName: string };
  drmAvailable: boolean;
  now?: Date;
}

const PLAYABLE_MEDIA_TYPES = ["main", "channel"];
const STREAM_PROTOCOLS = ["dash", "hls"];
const PROGRESSIVE_FILE_EXTENSION = /\.(mp4|m4v|mov|mkv|webm)$/i;

function canDecrypt(asset: CmsAsset, context: PlayabilityContext): boolean {
  const hasEncryptedDash = !!asset.medias?.some((media) =>
    media.mediaResources?.some(
      (resource) => resource.protocol === "dash" && resource.isEncrypted
    )
  );
  return (
    context.drmAvailable &&
    hasEncryptedDash &&
    hasVerimatrixDrm(asset.alternativeIds)
  );
}

function isInUploadStorage(url: URL, context: PlayabilityContext): boolean {
  const storage = context.uploadStorage;
  if (!storage?.host) return false;
  const firstSegment = url.pathname.split("/").filter(Boolean)[0];
  return (
    url.hostname.toLowerCase() === storage.host.toLowerCase() &&
    firstSegment === storage.containerName
  );
}

function resourceProblems(
  resource: MediaResource,
  decryptable: boolean,
  context: PlayabilityContext
): UnplayableReason[] {
  if (!STREAM_PROTOCOLS.includes(resource.protocol)) {
    return [UnplayableReason.NOT_A_STREAM];
  }

  let url: URL;
  try {
    url = new URL(resource.url);
  } catch {
    return [UnplayableReason.INVALID_URL];
  }

  const problems: UnplayableReason[] = [];
  if (PROGRESSIVE_FILE_EXTENSION.test(url.pathname)) {
    problems.push(UnplayableReason.VIDEO_FILE);
  }
  if (isInUploadStorage(url, context)) {
    problems.push(UnplayableReason.UPLOAD_STORAGE);
  }
  const signedUntil = url.searchParams.get("se");
  if (signedUntil) {
    const expiry = Date.parse(signedUntil);
    const now = (context.now ?? new Date()).getTime();
    if (!Number.isNaN(expiry) && expiry <= now) {
      problems.push(UnplayableReason.LINK_EXPIRED);
    }
  }
  if (resource.isEncrypted && !decryptable) {
    problems.push(UnplayableReason.ENCRYPTED_WITHOUT_DRM);
  }
  return problems;
}

// Never test the stream host here: a new stream provider would be flagged.
export function isPlayableCmsAsset(
  asset: CmsAsset,
  context: PlayabilityContext
): CmsAssetPlayability {
  if (!asset.medias?.length) {
    return { playable: false, reasons: [UnplayableReason.NO_MEDIA] };
  }

  const playableMedias = asset.medias.filter(
    (media) =>
      PLAYABLE_MEDIA_TYPES.includes(media.type) && media.enabled !== false
  );
  const resources = playableMedias.flatMap(
    (media) => media.mediaResources ?? []
  );
  if (!resources.length) {
    return { playable: false, reasons: [UnplayableReason.NO_MAIN_MEDIA] };
  }

  const decryptable = canDecrypt(asset, context);
  const reasons = new Set<UnplayableReason>();
  for (const resource of resources) {
    const problems = resourceProblems(resource, decryptable, context);
    if (!problems.length) return { playable: true, reasons: [] };
    problems.forEach((problem) => reasons.add(problem));
  }
  return { playable: false, reasons: [...reasons] };
}
